import { Button, CircularProgress, FormControl, FormHelperText, InputLabel } from '@mui/material';
import classNames from 'classnames';
import { StatusCodes } from 'http-status-codes';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { CountryData } from 'react-phone-input-2';
import PhoneInput from 'react-phone-input-2';

import { ReactComponent as WarningIcon } from '../../../../../../../assets/icons/inputWarningIcon.svg';
import useMixpanel, { componentType } from '../../../../../../../hooks/useMixpanel';
import useIntakeData from '../../../../../../../services/intakeDataService/index';
import useInviteSmsService from '../../../../../../../services/intakeDataService/inviteSmsService/index';
import { smsSentStatusEnum } from '../../../../../../../services/intakeDataService/types';
import usePersonalInfoService from '../../../../../../../services/patientProfileService/personalInfo';
import {
  getPrefillMobilePhoneNumber,
  isValidPhone,
  PHONE_VALIDATION_MSG,
} from '../../../../../../../utils/phoneHelper';
import customConfirmAlert from '../../../../../../ConfirmAlert';
import { useOnBoardingDrawer } from '../../../OnBoardingDrawerProvider';
import overlayStyles from '../../PatientDetailesStep/PatientDetailesForm/index.module.scss';
import styles from './index.module.scss';

const FormContent = ({
  patientUuid,
  mixpanelComponent,
}: {
  patientUuid?: string;
  mixpanelComponent?: componentType;
}) => {
  const {
    contextData: { patient },
  } = useOnBoardingDrawer();
  const { sendInviteSms } = useInviteSmsService();
  const { state } = useIntakeData();
  const { track, trackClick } = useMixpanel();
  const { data: personalInfo } = usePersonalInfoService(patientUuid);
  const [isValid, setIsValid] = useState({
    value: false,
    message: '',
  });
  const hasPrefilledRef = useRef(false);
  const {
    control,
    getValues,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = useForm<{ phoneNumber: string }>({ defaultValues: { phoneNumber: '' } });

  const prefillPhoneNumber = getPrefillMobilePhoneNumber(personalInfo);

  useEffect(() => {
    if (!hasPrefilledRef.current && !getValues('phoneNumber') && prefillPhoneNumber) {
      hasPrefilledRef.current = true;
      setValue('phoneNumber', prefillPhoneNumber);
      setIsValid({ value: true, message: '' });
    }
  }, [prefillPhoneNumber, getValues, setValue]);

  const updateFlow = (smsSentStatus: smsSentStatusEnum, phoneNumber?: string) => {
    state.updateSMSStatus(smsSentStatus);
    state.updatePhoneNumber(phoneNumber ?? '');
  };

  const onSendSms = async (phoneNumber?: string, force?: boolean) => {
    if (patientUuid) {
      await sendInviteSms({
        phoneNumber: `+${phoneNumber}`,
        patientUuid,
        force,
      });
      updateFlow(smsSentStatusEnum.Done, phoneNumber);
    }
  };

  const showConflictAlert = (phoneNumber?: string) => {
    customConfirmAlert({
      title: `Number Already in System`,
      body: 'The number you have entered already exists in our system. Continuing will send a verification text to this phone number. Would you like to continue?',
      label: 'Continue',
      onClick: () => onSendSms(phoneNumber, true),
      classes: {
        overlay: overlayStyles.patientConfirmOverlay,
        body: overlayStyles.body,
      },
    });
  };

  const showErrorAlert = (errorCode?: number) => {
    customConfirmAlert({
      title: 'Error',
      body: (
        <div className={classNames(overlayStyles.body, 'flex flex-col items-center')}>
          <div>
            Contact{' '}
            <a href="mailto:support@onestep.co" className={overlayStyles.noLine}>
              support@onestep.co
            </a>{' '}
            for additional details.
          </div>
          <span className={overlayStyles.errorCode}>Error Code {errorCode}</span>
        </div>
      ),
      label: 'Try again',
      classes: {
        overlay: overlayStyles.patientConfirmOverlay,
        body: overlayStyles.body,
        secondary: overlayStyles.hidden,
      },
    });
  };

  const setValidationMessage = (message: string) => setIsValid({ ...isValid, message });

  const onSend = async (form: { phoneNumber: string }) => {
    try {
      trackClick({
        action: 'send_invite_patient',
        component: mixpanelComponent ?? componentType.registrationFlow,
      });
      await onSendSms(form?.phoneNumber);
    } catch (error) {
      const isNumberExistError = error?.response?.status === StatusCodes.CONFLICT;
      if (isNumberExistError) {
        showConflictAlert(form?.phoneNumber);
      } else if (error?.response?.status === StatusCodes.NOT_FOUND) {
        setValidationMessage(PHONE_VALIDATION_MSG);
      } else {
        showErrorAlert(error?.response?.status);
        track('error_dialogue', {
          error_code: error?.response?.status,
        });
      }
    }
  };

  return (
    <form
      className={classNames(styles.phoneNumberWrapper, 'flex mb-4 pb-2')}
      onSubmit={handleSubmit(onSend)}
    >
      <FormControl className="relative flex-grow">
        <InputLabel className={styles.phoneLabel}>Phone Number</InputLabel>

        <Controller
          name="phoneNumber"
          control={control}
          render={({ field }) => (
            <PhoneInput
              inputClass={styles.input}
              country="us"
              containerClass={styles.phoneInputContainer}
              buttonClass={styles.dropDownBtn}
              defaultMask="...-...-...."
              alwaysDefaultMask
              placeholder=""
              value={field.value}
              onChange={(value: string, country: CountryData) => {
                field.onChange(value);
                setIsValid({
                  value: isValidPhone(value, country),
                  message: '',
                });
              }}
              onBlur={() => {
                field.onBlur();
                if (!isValid.value) {
                  setValidationMessage(PHONE_VALIDATION_MSG);
                }
              }}
            />
          )}
        />
        {isValid.message ? (
          <FormHelperText className={classNames(styles.helperText, styles.error)}>
            <WarningIcon />
            {isValid.message}
          </FormHelperText>
        ) : patient?.poa ? (
          <FormHelperText className={styles.helperText}>
            If the patient is using a POA’s (power of attorney) phone, enter the POA’s number
          </FormHelperText>
        ) : null}
      </FormControl>
      <Button
        disabled={!isValid.value}
        type="submit"
        variant="contained"
        className={styles.sentBtn}
        color="primary"
      >
        {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Send Invite'}
      </Button>
    </form>
  );
};
export default observer(FormContent);
